import React, { useState } from 'react';
import { Save, X } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import LiquidGlassButton from '../LiquidGlassButton';

const PasswordChangeForm = ({ user, adminUser, onPasswordChanged, onUserUpdated, onCancel, showSuccess, showError }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // Prüfen ob der aktuelle Benutzer ein Admin ist und nicht seinen eigenen Account bearbeitet
  const isAdmin = user.role === 'admin';
  const isOwnAccount = user.userId === adminUser.id;
  const skipCurrentPassword = isAdmin && !isOwnAccount;
  
  console.log('PasswordChangeForm: User roles/IDs:', {
    currentUserId: user.userId,
    targetUserId: adminUser.id,
    isOwnAccount,
    isAdmin,
    skipCurrentPassword
  });

  const handlePasswordChange = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError(t('admin.password.mismatch'));
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError(t('admin.password.minLength'));
      return;
    }

    if (!skipCurrentPassword && !passwordForm.currentPassword) {
      setPasswordError(t('admin.password.currentRequired'));
      return;
    }

    setPasswordLoading(true);
    setPasswordError('');

    try {
      const payload = {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
        userId: adminUser.id
      };
      
      console.log('PasswordChangeForm: Sending payload:', payload);
      
      const response = await fetch('/api/admin/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || t('admin.password.error'));
      }
      // Eigenes Passwort: alte Sitzung ist serverseitig beendet, neues Token übernehmen
      if (result.token && onUserUpdated) onUserUpdated({ token: result.token });

      if (showSuccess) {
        showSuccess(t('admin.password.changed'));
      } else {
        alert(t('admin.password.changed'));
      }
      onPasswordChanged();
    } catch (error) {
      console.error('Password change error:', error);
      setPasswordError(error.message);
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="relative group mt-4">
      {/* Liquid Glass Background */}
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.4)]'
        : 'glass-light-card'
      } rounded-3xl`}></div>
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
        : ''
      } rounded-3xl`}></div>
      
      
      <div className="relative p-6">
        {/* Liquid Glass Header */}
        <div className="flex items-center space-x-3 mb-6">
          {/* Liquid Glass Key Icon */}
          <div className="relative group/icon">
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-2xl blur-sm"></div>
            <div className="relative bg-gradient-to-br from-amber-600/60 via-amber-700/50 to-amber-800/60 backdrop-blur-xl w-10 h-10 rounded-2xl flex items-center justify-center shadow-[0_8px_32px_rgba(245,158,11,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20">
              <Save className="w-5 h-5 text-white/90 drop-shadow-lg" />
              <div className="absolute top-1 left-1 w-2 h-2 bg-white/30 rounded-full blur-sm"></div>
            </div>
          </div>
          <div>
            <h5 className={`text-lg font-bold ${isDarkMode
              ? 'bg-gradient-to-r from-white via-amber-200 to-white bg-clip-text text-transparent drop-shadow-lg'
              : 'text-slate-900'
            }`}>
              {isOwnAccount ? t('admin.password.title') : t('admin.password.titleFor', { username: adminUser.username })}
            </h5>
          </div>
        </div>
        
        {/* Liquid Glass Error Message */}
        {passwordError && (
          <div className="relative group/error mb-6">
            <div className={`absolute inset-0 ${isDarkMode
              ? 'bg-red-500/10 border border-red-300/30 backdrop-blur-xl shadow-[0_8px_32px_rgba(239,68,68,0.2)]'
              : 'bg-red-50 border border-red-200'
            } rounded-2xl`}></div>
            <div className={`absolute inset-0 rounded-2xl ${isDarkMode ? 'bg-gradient-to-r from-red-400/10 to-pink-400/10' : ''}`}></div>
            <div className="relative p-4 flex items-center">
              <div className="w-2 h-2 bg-red-400/80 rounded-full mr-3 animate-pulse"></div>
              <p className={`${isDarkMode ? 'text-red-200' : 'text-red-700'} text-sm font-medium drop-shadow-lg`}>{passwordError}</p>
            </div>
          </div>
        )}
      
        {/* Liquid Glass Input Fields */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {!skipCurrentPassword && (
            <div className="relative group/input">
              <label className={`block text-sm font-semibold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-700'} mb-3`}>{t('admin.password.current')}</label>
              <div className="relative">
                {/* Glass Input Background */}
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gray-800/30 border border-white/30 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]'
                  : 'bg-white border border-slate-200'
                } rounded-2xl`}></div>
                
                <input 
                  type="password" 
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                  placeholder={t('admin.password.current')}
                  disabled={passwordLoading}
                  className={`relative w-full px-4 py-3 bg-transparent border-0 rounded-2xl focus:outline-none transition-all duration-300 disabled:opacity-50 ${isDarkMode
                    ? 'text-white placeholder-white/60 focus:ring-2 focus:ring-amber-400/50 focus:shadow-[0_0_20px_rgba(245,158,11,0.4)] hover:shadow-[0_0_15px_rgba(255,255,255,0.2)]'
                    : 'text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-brand/40'
                  }`}
                />
                
                {/* Focus Highlight */}
                <div className="absolute inset-0 rounded-2xl border border-transparent group-focus-within/input:border-amber-400/50 transition-all duration-300 pointer-events-none"></div>
              </div>
            </div>
          )}
          
          {/* New Password Field */}
          <div className="relative group/input">
            <label className={`block text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'} mb-3 drop-shadow-lg`}>{t('admin.password.new')}</label>
            <div className="relative">
              {/* Glass Input Background */}
              <div className={`absolute inset-0 ${isDarkMode 
                ? 'bg-gray-800/30 border border-white/30' 
                : 'bg-white/50 border border-blue-200/40'
              } backdrop-blur-xl rounded-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]`}></div>
              
              <input 
                type="password" 
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                placeholder={t('admin.password.new')}
                disabled={passwordLoading}
                className={`relative w-full px-4 py-3 bg-transparent border-0 rounded-2xl focus:ring-2 focus:ring-amber-400/50 focus:outline-none transition-all duration-300 disabled:opacity-50 ${isDarkMode 
                  ? 'text-white placeholder-white/60' 
                  : 'text-gray-800 placeholder-gray-500'
                } focus:shadow-[0_0_20px_rgba(245,158,11,0.4)] hover:shadow-[0_0_15px_rgba(255,255,255,0.2)]`}
              />
              
              {/* Focus Highlight */}
              <div className="absolute inset-0 rounded-2xl border border-transparent group-focus-within/input:border-amber-400/50 transition-all duration-300 pointer-events-none"></div>
            </div>
          </div>
          
          {/* Confirm Password Field */}
          <div className="relative group/input">
            <label className={`block text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'} mb-3 drop-shadow-lg`}>{t('admin.password.confirm')}</label>
            <div className="relative">
              {/* Glass Input Background */}
              <div className={`absolute inset-0 ${isDarkMode 
                ? 'bg-gray-800/30 border border-white/30' 
                : 'bg-white/50 border border-blue-200/40'
              } backdrop-blur-xl rounded-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]`}></div>
              
              <input 
                type="password" 
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                placeholder={t('admin.password.confirm')}
                disabled={passwordLoading}
                className={`relative w-full px-4 py-3 bg-transparent border-0 rounded-2xl focus:ring-2 focus:ring-amber-400/50 focus:outline-none transition-all duration-300 disabled:opacity-50 ${isDarkMode 
                  ? 'text-white placeholder-white/60' 
                  : 'text-gray-800 placeholder-gray-500'
                } focus:shadow-[0_0_20px_rgba(245,158,11,0.4)] hover:shadow-[0_0_15px_rgba(255,255,255,0.2)]`}
              />
              
              {/* Focus Highlight */}
              <div className="absolute inset-0 rounded-2xl border border-transparent group-focus-within/input:border-amber-400/50 transition-all duration-300 pointer-events-none"></div>
            </div>
          </div>
        </div>
      
        {/* Liquid Glass Action Buttons */}
        <div className="flex space-x-3">
          {/* Save Button */}
          <LiquidGlassButton
            onClick={handlePasswordChange}
            disabled={
              passwordLoading || 
              (!skipCurrentPassword && !passwordForm.currentPassword) || 
              !passwordForm.newPassword || 
              !passwordForm.confirmPassword
            }
            loading={passwordLoading}
            variant="success"
            size="medium"
            icon={Save}
            iconPosition="left"
          >
            {t('common.save')}
          </LiquidGlassButton>

          {/* Cancel Button */}
          <LiquidGlassButton
            onClick={onCancel}
            disabled={passwordLoading}
            variant="secondary"
            size="medium"
            icon={X}
            iconPosition="left"
          >
            {t('common.cancel')}
          </LiquidGlassButton>
        </div>
      </div>
    </div>
  );
};

export default PasswordChangeForm;